import { createActionCreator } from 'deox';
import { Entity, Paging, EntityComparison, Country } from '@/types';

import { EntityForm } from '@/services/service-directory/entity';

export const fetchFeaturedEntitiesActions = {
  request: createActionCreator('FETCH_FEATURED_ENTITIES_REQUEST', (resolve) => (recaptchaToken?: string) =>
    resolve({ recaptchaToken })
  ),
  success: createActionCreator('FETCH_FEATURED_ENTITIES_SUCCESS', (resolve) => ({ data }: { data: Entity[] }) =>
    resolve({ data })
  ),
  failure: createActionCreator('FETCH_FEATURED_ENTITIES_FAILURE', (resolve) => (error: any) => resolve({ error })),
};

export const clearEntitiesActions = {
  request: createActionCreator('CLEAR_ENTITIES'),
};

export const fetchEntitiesActions = {
  request: createActionCreator(
    'FETCH_ENTITIES_REQUEST',
    (resolve) => (
      pageNum: number,
      pageSize: number,
      searchEntity: string,
      filterValues?: { [key: string]: string[] },
      referer?: string,
      location?: string,
      region?: string,
      country?: string,
      listOrders?: string,
      isAdminAffect?: boolean,
      tag?: string,
      feature?: string,
      captchaToken?: string
    ) =>
      resolve({
        pageNum,
        pageSize,
        searchEntity,
        referer,
        filterValues,
        location,
        region,
        country,
        listOrders,
        isAdminAffect,
        tag,
        feature,
        captchaToken,
      })
  ),
  success: createActionCreator(
    'FETCH_ENTITIES_SUCCESS',
    (resolve) => ({ data, paging, keyword }: { data: Entity[]; paging: Paging; keyword: string }) =>
      resolve({ data, paging, keyword })
  ),
  failure: createActionCreator('FETCH_ENTITIES_FAILURE', (resolve) => (error: any) => resolve({ error })),
};

export const fetchEntitiesDirectoryActions = {
  request: createActionCreator(
    'FETCH_ENTITIES_DIRECTORY_REQUEST',
    (resolve) => (pageNum: number, pageSize: number, searchEntity: string, dataSource?: string) =>
      resolve({ pageNum, pageSize, searchEntity, dataSource })
  ),
  success: createActionCreator(
    'FETCH_ENTITIES_DIRECTORY_SUCCESS',
    (resolve) => ({ data, paging }: { data: Entity[]; paging: Paging }) => resolve({ data, paging })
  ),
  failure: createActionCreator('FETCH_ENTITIES_DIRECTORY_FAILURE', (resolve) => (error: any) => resolve({ error })),
};

export const loadMoreEntitiesActions = {
  request: createActionCreator(
    'LOAD_MORE_ENTITIES_REQUEST',
    (resolve) => (
      pageNum: number,
      pageSize: number,
      searchEntity: string,
      filterValues?: { [key: string]: string[] },
      referer?: string,
      location?: string,
      region?: string,
      country?: string,
      listOrders?: string,
      isAdminAffect?: boolean,
      tag?: string,
      feature?: string,
      recaptchaToken?: string
    ) =>
      resolve({
        pageNum,
        pageSize,
        searchEntity,
        referer,
        filterValues,
        location,
        region,
        country,
        listOrders,
        isAdminAffect,
        tag,
        feature,
        recaptchaToken,
      })
  ),
  success: createActionCreator(
    'LOAD_MORE_ENTITIES_SUCCESS',
    (resolve) => ({ data, paging }: { data: Entity[]; paging: Paging }) => resolve({ data, paging })
  ),
  failure: createActionCreator('LOAD_MORE_ENTITIES_FAILURE', (resolve) => (error: any) => resolve({ error })),
};

export const fetchEntityBySubdomainActions = {
  request: createActionCreator('FETCH_ENTITY_BY_SUBDOMAIN_REQUEST', (resolve) => (subdomain: string) =>
    resolve({ subdomain })
  ),
  success: createActionCreator('FETCH_ENTITY_BY_SUBDOMAIN_SUCCESS', (resolve) => (entity: Entity) =>
    resolve({ entity })
  ),
  failure: createActionCreator('FETCH_ENTITY_BY_SUBDOMAIN_FAILURE', (resolve) => (error: any) => resolve({ error })),
};

export const fetchEntityByIdActions = {
  request: createActionCreator('FETCH_ENTITY_BY_ID_REQUEST', (resolve) => (entityId: string) => resolve({ entityId })),
  success: createActionCreator('FETCH_ENTITY_BY_ID_SUCCESS', (resolve) => (entity: Entity) => resolve({ entity })),
  failure: createActionCreator('FETCH_ENTITY_BY_ID_FAILURE', (resolve) => (error: any) => resolve({ error })),
};

export const fetchEntityViewOnlyActions = {
  request: createActionCreator(
    'FETCH_ENTITY_VIEW_ONLY_REQUEST',
    (resolve) => (params: {
      subdomain?: string;
      entityId?: string;
      isAdminAffect?: boolean;
      recaptchaToken?: string;
    }) => resolve(params)
  ),
  success: createActionCreator('FETCH_ENTITY_VIEW_ONLY_SUCCESS', (resolve) => (entity: Entity) => resolve({ entity })),
  failure: createActionCreator('FETCH_ENTITY_VIEW_ONLY_FAILURE', (resolve) => (error: any) => resolve({ error })),
  reset: createActionCreator('FETCH_ENTITY_VIEW_ONLY_RESET'),
};

export const fetchEntityDashboardActions = {
  request: createActionCreator(
    'FETCH_ENTITY_DASHBOARD_REQUEST',
    (resolve) => (params: { subdomain?: string; entityId?: string; recaptchaToken?: string }) => resolve(params)
  ),
  success: createActionCreator('FETCH_ENTITY_DASHBOARD_SUCCESS', (resolve) => (entity: Entity) => resolve({ entity })),
  failure: createActionCreator('FETCH_ENTITY_DASHBOARD_FAILURE', (resolve) => (error: any) => resolve({ error })),
};

export const fetchEntityInvitationActions = {
  request: createActionCreator('FETCH_ENTITY_INVITATION_REQUEST', (resolve) => (invitationCode: string) =>
    resolve({ invitationCode })
  ),
  success: createActionCreator('FETCH_ENTITY_INVITATION_SUCCESS', (resolve) => (entity: Entity) => resolve({ entity })),
  failure: createActionCreator('FETCH_ENTITY_INVITATION_FAILURE', (resolve) => (error: Error) => resolve({ error })),
};

export const fetchEntitiesComparisonActions = {
  request: createActionCreator(
    'FETCH_ENTITIES_COMPARISON_REQUEST',
    (resolve) => (
      pageNumber?: number,
      pageSize?: number,
      complianceGroups?: string,
      entityName?: string,
      countries?: string,
      sort?: string
    ) => resolve({ pageNumber, pageSize, complianceGroups, entityName, countries, sort })
  ),
  success: createActionCreator(
    'FETCH_ENTITIES_COMPARISON_SUCCESS',
    (resolve) => ({ data, paging }: { data: EntityComparison[]; paging: Paging }) => resolve({ data, paging })
  ),
  failure: createActionCreator('FETCH_ENTITIES_COMPARISON_FAILURE', (resolve) => (error: any) => resolve({ error })),
  reset: createActionCreator('FETCH_ENTITIES_COMPARISON_RESET'),
};

export const loadMoreEntitiesComparisonActions = {
  request: createActionCreator(
    'LOAD_MORE_ENTITIES_COMPARISON_REQUEST',
    (resolve) => (
      pageNumber?: number,
      pageSize?: number,
      complianceGroups?: string,
      entityName?: string,
      countries?: string,
      sort?: string
    ) => resolve({ pageNumber, pageSize, complianceGroups, entityName, countries, sort })
  ),
  success: createActionCreator(
    'LOAD_MORE_ENTITIES_COMPARISON_SUCCESS',
    (resolve) => ({ data, paging }: { data: EntityComparison[]; paging: Paging }) => resolve({ data, paging })
  ),
  failure: createActionCreator('LOAD_MORE_ENTITIES_COMPARISON_FAILURE', (resolve) => (error: any) =>
    resolve({ error })
  ),
};

export const createOrUpdateEntityActions = {
  request: createActionCreator(
    'CREATE_OR_UPDATE_ENTITY_REQUEST',
    (resolve) => (entityId: string, entity: EntityForm, getDefaultApis?: boolean, adding?: boolean, cb?: Function) =>
      resolve({ entityId, entity, getDefaultApis, adding, cb })
  ),
  success: createActionCreator('CREATE_OR_UPDATE_ENTITY_SUCCESS'),
  failure: createActionCreator('CREATE_OR_UPDATE_ENTITY_FAILURE', (resolve) => (error: Error) => resolve({ error })),
};

export const reloadDefaultEntityActions = {
  request: createActionCreator('RELOAD_DEFAULT_ENTITY_REQUEST', (resolve) => (organisationId: string, type: string) =>
    resolve({ organisationId, type })
  ),
  success: createActionCreator('RELOAD_DEFAULT_ENTITY_SUCCESS', (resolve) => () => resolve()),
  failure: createActionCreator('RELOAD_DEFAULT_ENTITY_FAILURE', (resolve) => (error: Error) => resolve({ error })),
};

export const deleteEntityActions = {
  request: createActionCreator('DELETE_ENTITY_REQUEST', (resolve) => (entityId: string) => resolve({ entityId })),
  success: createActionCreator('DELETE_ENTITY_SUCCESS', (resolve) => (entityId: string) => resolve({ entityId })),
  failure: createActionCreator('DELETE_ENTITY_FAILURE', (resolve) => (error: any) => resolve({ error })),
};

export const generateIndexesActions = {
  request: createActionCreator('GENERATE_INDEXES_REQUEST'),
  success: createActionCreator('GENERATE_INDEXES_SUCCESS'),
  failure: createActionCreator('GENERATE_INDEXES_FAILURE', (resolve) => (error: any) => resolve({ error })),
};

export const fetchCountriesEntityActions = {
  request: createActionCreator('FETCH_COUNTRIES_ENTITY_REQUEST', (resolve) => () => resolve()),
  success: createActionCreator(
    'FETCH_COUNTRIES_ENTITY_SUCCESS',
    (resolve) => (data: Country[], paging: Paging, meta: { authors: string[] }) => resolve({ data, paging, meta })
  ),
  failure: createActionCreator('FETCH_COUNTRIES_ENTITY_FAILURE', (resolve) => (error: any) => resolve({ error })),
  reset: createActionCreator('FETCH_COUNTRIES_ENTITY_RESET'),
};

export const fetchRelatedEntitiesActions = {
  request: createActionCreator(
    'FETCH_RELATED_ENTITIES_REQUEST',
    (resolve) => (currentEntityId?: string, total?: number, country?: string, region?: string, entityType?: string) =>
      resolve({ currentEntityId, total, country, region, entityType })
  ),
  success: createActionCreator('FETCH_RELATED_ENTITIES_SUCCESS', (resolve) => ({ data }: { data: Entity[] }) =>
    resolve({ data })
  ),
  failure: createActionCreator('FETCH_RELATED_ENTITIES_FAILURE', (resolve) => (error: any) => resolve({ error })),
};
